// Opens a document in a new browser tab.
//
// Two tricky platform constraints force the shape of this code:
//
//   1. Chrome blocks top-level navigation to `data:` URLs (since 2020),
//      so `window.open(dataUrl, '_blank')` opens blank. We convert the
//      dataUrl to a Blob and open a blob: URL instead.
//
//   2. `window.open(url, '_blank', 'noopener')` returns `null` per spec
//      (the opener is severed, so there's no handle to return). For
//      paths that need to navigate the tab later (async signed-URL
//      lookups), we have to omit `noopener` and null out `win.opener`
//      ourselves before navigation.

// Decode a data: URL into its raw bytes + MIME. Returns null if the
// input isn't a valid data URL. Shared by the viewer / download / PDF
// merge paths so each caller doesn't re-derive the atob/base64 split.
function dataUrlToBytes(dataUrl) {
  if (!dataUrl || !dataUrl.startsWith('data:')) return null;
  try {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] || 'application/octet-stream';
    if (!base64) return null;
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    return { bytes, mime };
  } catch (err) {
    console.warn('[openDocumentInTab] dataUrl decode failed:', err);
    return null;
  }
}

// Convert a data: URL into a blob: URL that's safe to open in a new tab.
function dataUrlToBlobUrl(dataUrl) {
  const decoded = dataUrlToBytes(dataUrl);
  if (!decoded) return null;
  return URL.createObjectURL(new Blob([decoded.bytes], { type: decoded.mime }));
}

// Resolve a document row to its raw bytes. Inline dataUrl is fast-path;
// falls back to a signed storage URL via the supplied async resolver
// (`getStoragePathUrl(storagePath) → Promise<string | null>`). Keeps
// the supabaseStore dependency out of this utility — callers inject
// their own signed-URL fetcher. Returns null when the doc carries
// neither inline bytes nor a reachable storage path.
export async function resolveDocumentBytes(doc, getStoragePathUrl) {
  const inline = doc?.fileDataUrl || doc?.dataUrl;
  const direct = dataUrlToBytes(inline);
  if (direct) return direct;
  if (doc?.storagePath && typeof getStoragePathUrl === 'function') {
    try {
      const signed = await getStoragePathUrl(doc.storagePath);
      if (!signed) return null;
      const res = await fetch(signed);
      if (!res.ok) return null;
      const buf = new Uint8Array(await res.arrayBuffer());
      const mime = res.headers.get('content-type') || 'application/octet-stream';
      return { bytes: buf, mime };
    } catch (err) {
      console.warn('[openDocumentInTab] storage fetch failed:', err);
    }
  }
  return null;
}

// Resolve a document row to a blob: URL the browser can open directly
// (via <iframe src>, <a download>, window.open, etc). Returns null
// when the doc can't be resolved. Caller is responsible for revoking
// the URL once it's done with it (URL.revokeObjectURL).
export async function resolveDocumentBlobUrl(doc, getStoragePathUrl) {
  const decoded = await resolveDocumentBytes(doc, getStoragePathUrl);
  if (!decoded) return null;
  return URL.createObjectURL(new Blob([decoded.bytes], { type: decoded.mime }));
}

// Given any inline URL the app hands us (dataUrl, blob URL, or a normal
// http(s) URL), return one that `window.open` can actually load.
export function toOpenableUrl(url) {
  if (!url) return null;
  if (url.startsWith('data:')) return dataUrlToBlobUrl(url);
  return url; // blob: and http(s): are fine as-is
}

// Open a document immediately when we already have a URL.
// Used when the doc has an inline dataUrl / fileDataUrl cached.
export function openDocumentUrl(url) {
  const openable = toOpenableUrl(url);
  if (!openable) return false;
  window.open(openable, '_blank', 'noopener,noreferrer');
  return true;
}

// Open a URL in a new browser window pinned to the right half of
// the screen — so the user can line it up next to the main app on
// the left for split-screen comparison. Used by the document
// library, the map popout, and anywhere else we want a companion
// window.
//
// Named `windowName` keeps repeated opens from stacking duplicate
// windows — a second call with the same name brings the existing
// window forward and navigates it to the new URL.
export function openHalfScreenWindow(path, windowName = '_blank') {
  const screenW = (typeof window !== 'undefined' && window.screen?.availWidth) || 1280;
  const screenH = (typeof window !== 'undefined' && window.screen?.availHeight) || 800;
  const screenL = (typeof window !== 'undefined' && window.screen?.availLeft) || 0;
  const screenT = (typeof window !== 'undefined' && window.screen?.availTop) || 0;
  const w = Math.max(720, Math.round(screenW / 2));
  const h = screenH;
  const left = screenL + (screenW - w);
  const top = screenT;
  const features = [
    `width=${w}`, `height=${h}`,
    `left=${left}`, `top=${top}`,
    'resizable=yes', 'scrollbars=yes', 'menubar=no', 'toolbar=no',
    'location=yes', 'status=no',
  ].join(',');
  return window.open(path, windowName, features);
}

// True when the current window is small enough that a half-screen
// popup would be unusable, OR the app is running as an installed PWA
// (where window.open() either gets blocked outright or spawns a Safari
// tab outside the app shell). Both cases want same-tab navigation.
function preferSameTabNavigation() {
  if (typeof window === 'undefined') return false;
  const narrow = (window.innerWidth || 0) <= 900;
  const standalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator?.standalone === true;
  return narrow || standalone;
}

// Open the in-app /documents page in a half-screen companion window
// — or, on phones / installed PWAs, navigate the current tab so the
// user doesn't end up with a blocked popup or a stranded Safari tab.
// Accepts either a pre-filter query string or an options object. The
// object form lets a dashboard open the library in an attach workflow.
export function openDocumentLibraryWindow(query = '') {
  let path = '/documents';
  if (query && typeof query === 'object') {
    const qs = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value != null && value !== '') qs.set(key, value);
    });
    const suffix = qs.toString();
    path = suffix ? `/documents?${suffix}` : '/documents';
  } else if (query) {
    path = `/documents?q=${encodeURIComponent(query)}`;
  }
  if (preferSameTabNavigation()) {
    window.location.assign(path);
    return null;
  }
  return openHalfScreenWindow(path, 'shadowland-document-library');
}

// Open the in-app /map (or /map-only for the chrome-less variant) in
// a half-screen companion window. Same mobile fallback as the doc
// library: navigate the current tab on small screens / installed PWAs.
// Pass params as a plain object; any value that's not null/undefined
// becomes a URL query param.
export function openMapWindow(params = {}, { chromeless = true } = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') qs.append(k, v);
  });
  // Cache-buster so repeated opens with the same params re-run the
  // zoom effect on the existing tab (AssignmentMap keys its re-run
  // on location.search).
  if (!qs.has('t')) qs.set('t', String(Date.now()));
  const base = chromeless ? '/map-only' : '/map';
  const path = qs.toString() ? `${base}?${qs.toString()}` : base;
  if (preferSameTabNavigation()) {
    window.location.assign(path);
    return null;
  }
  return openHalfScreenWindow(path, 'shadowland-map');
}

// Open a document whose URL needs async resolution. The tab is opened
// synchronously inside the click (so the browser keeps the user-gesture
// context), then navigated once `resolveUrl` returns.
//
// Returns a promise that resolves to `true` on success, `false` if the
// URL couldn't be resolved (tab is auto-closed in that case).
export async function openDocumentAsync(resolveUrl) {
  // NOTE: omit 'noopener' here — with that flag window.open returns
  // null per spec, and we'd have no handle to navigate.
  const win = window.open('about:blank', '_blank');
  let url = null;
  try { url = await resolveUrl(); }
  catch (err) { console.error('[openDocumentInTab] resolveUrl threw:', err); }
  const openable = toOpenableUrl(url);
  if (openable && win && !win.closed) {
    try { win.opener = null; } catch { /* cross-origin — ignored */ }
    win.location.href = openable;
    return true;
  }
  if (win && !win.closed) win.close();
  return false;
}
